import { useState, useEffect } from 'react'
import { useApi } from '@/hooks/useApi'
import { Button } from '@/components/ui'
import {
  IconCopy, IconCheck, IconEye, IconEyeOff, IconRefresh, IconAlertTriangle, IconX,
} from '@/components/icons/Icons'

/*
  El acceso de este proyecto para un agente de IA.

  Para qué sirve: el código de un cliente vive en otro repositorio, con su
  propia sesión de Claude Code, que no ve el backlog. Antes había que
  contárselo a mano y el trabajo se repetía — en FUTURA se pidieron en octubre
  cosas que estaban en la lista desde abril. Acá se copia el acceso y se pega
  en ese chat, y queda conectado.

  La llave es de ESTE proyecto: no abre ningún otro. Eso importa porque hay
  clientes de marca blanca cuyo agente no debe ver el backlog de otro.
*/
interface Props {
  proyectoId: number
  proyectoNombre: string
}

interface EstadoLlave {
  token: string | null
  generada_en: string | null
  proyecto: string
  tareas_abiertas: number
}

const API = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** El comando pelado, para quien ya sabe qué hacer con él. */
function comando(token: string): string {
  return `curl -s -H "X-Tareas-Key: ${token}" "${API}/api/tareas/backlog"`
}

/**
 * El bloque completo para pegar en el CLAUDE.md o AGENTS.md del otro chat.
 *
 * Lleva el porqué y no sólo el cómo: un agente que entiende para qué sirve
 * busca en el backlog antes de abrir trabajo nuevo, que es justamente el
 * problema que esto resuelve.
 */
function bloqueParaIA(token: string, nombre: string): string {
  return `## El backlog de este proyecto vive en el ERP de 1Conecta

La lista de lo que el cliente pidió **no está en este repositorio**: vive en el
ERP, que es donde aterrizan las conversaciones. Leerla es lo primero de cada
sesión, antes de planificar nada.

\`\`\`bash
curl -s -H "X-Tareas-Key: ${token}" \\
  "${API}/api/tareas/backlog"
\`\`\`

Devuelve Markdown, ordenado por prioridad. Se lee tal cual.

**Por qué importa, no sólo cómo.** Mientras esta sesión no veía esa lista, el
trabajo se repetía: se construía algo que ya estaba pedido con otro nombre, y
nadie lo notaba durante meses. **Buscá en el backlog antes de abrir trabajo
nuevo.**

### Cerrar una tarea

Cuando termines algo que estaba en la lista, cerralo:

\`\`\`bash
curl -s -X POST -H "X-Tareas-Key: ${token}" -H "Content-Type: application/json" \\
  -d '{"nota":"qué hiciste realmente","autor":"claude-code"}' \\
  "${API}/api/tareas/<id>/cerrar"
\`\`\`

- La nota es **obligatoria** (mín. 10 caracteres) y queda **firmada** con tu
  nombre y la fecha en el historial de la tarea. Un cierre equivocado tiene que
  poder rastrearse para deshacerse.
- Cerrá sólo lo que puedas verificar. Una tarea cerrada por las dudas desaparece
  de la lista y nadie vuelve a mirarla — es peor que dejarla abierta.
- Es idempotente: reintentar es seguro.

### Crear, editar y organizar

La misma llave escribe. Todo contra \`${API}\` y con la cabecera
\`X-Tareas-Key\`:

| Qué | Dónde |
|---|---|
| Crear una tarea o un hito | \`POST /api/tareas/backlog/tareas\` |
| Editar una tarea | \`PATCH /api/tareas/backlog/tareas/<id>\` |
| Mover una tarea bajo un hito | el mismo PATCH con \`{"parent_id": N}\` (\`null\` la saca) |
| Dependencias | \`POST /api/tareas/backlog/tareas/<id>/dependencias\` |

\`\`\`bash
# Crear un hito y colgarle una tarea que espera a la #312
curl -s -X POST -H "X-Tareas-Key: ${token}" -H "Content-Type: application/json" \
  -d '{"titulo":"Migración del checkout","descripcion":"qué se va a lograr","prioridad":"alta"}' \
  "${API}/api/tareas/backlog/tareas"

curl -s -X POST -H "X-Tareas-Key: ${token}" -H "Content-Type: application/json" \
  -d '{"titulo":"Conectar la pasarela","parent_id":<id del hito>,"depende_de":[312],"tipo":"tarea"}' \
  "${API}/api/tareas/backlog/tareas"
\`\`\`

**Un hito no es una entidad aparte:** es una tarea con hijas colgadas de
\`parent_id\`. Para armar uno, creá la tarea y movele las hijas debajo.

Campos que acepta el PATCH: \`titulo\`, \`descripcion\`, \`notas\`,
\`prioridad\` (alta/media/baja), \`tipo\` (tarea/mejora/bug), \`estado\`,
\`etiquetas\`, \`horas_estimadas\`, \`fecha_limite\`, \`parent_id\`.

**Lo que creás nace marcado como tuyo** (\`origen: agente\`) y cada escritura
queda registrada. No es vigilancia: es para que quien mire el tablero sepa qué
propuso un agente y qué se acordó con el cliente.

### Qué NO permite esta llave

- **Borrar.** No hay papelera y un borrado desde un agente no se deshace. Lo que
  ya no va se cancela: \`{"estado":"cancelada","motivo":"por qué"}\`, y queda el
  rastro.
- **Cerrar por PATCH.** \`estado: completada\` rebota a propósito: cerrar pasa
  por \`/cerrar\`, que exige contar qué se hizo.
- **Tocar el dinero.** Montos, tipo de cobro, aprobación y facturas quedan
  fuera: son lo que se le cobra al cliente.
- **Salir de ${nombre}.** No ve ni escribe en ningún otro proyecto, ni en
  ninguna otra parte del ERP. Si necesitás algo fuera de eso, pedilo — no
  busques otra entrada.

**La llave es un secreto.** Va en el \`.env\` del repositorio (que debe estar en
\`.gitignore\`), nunca escrita dentro de un archivo que se commitea.`
}

export default function IntegracionesProyecto({ proyectoId, proyectoNombre }: Props) {
  const api = useApi()
  const [estado, setEstado] = useState<EstadoLlave | null>(null)
  const [cargando, setCargando] = useState(true)
  const [visible, setVisible] = useState(false)
  const [copiado, setCopiado] = useState<'comando' | 'bloque' | null>(null)
  const [confirmando, setConfirmando] = useState<'regenerar' | 'revocar' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setCargando(true)
    api.get<EstadoLlave>(`/api/tareas/proyecto/${proyectoId}/llave`)
      .then(setEstado)
      .catch(() => setError('No se pudo leer el estado de la llave.'))
      .finally(() => setCargando(false))
  }, [proyectoId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function generar() {
    setError(null)
    setConfirmando(null)
    try {
      const r = await api.post<{ token: string; generada_en: string }>(
        `/api/tareas/proyecto/${proyectoId}/llave`, {},
      )
      setEstado(e => e ? { ...e, token: r.token, generada_en: r.generada_en } : e)
      setVisible(true)
    } catch {
      setError('No se pudo generar la llave.')
    }
  }

  async function revocar() {
    setError(null)
    setConfirmando(null)
    try {
      await api.del(`/api/tareas/proyecto/${proyectoId}/llave`)
      setEstado(e => e ? { ...e, token: null, generada_en: null } : e)
      setVisible(false)
    } catch {
      setError('No se pudo revocar la llave.')
    }
  }

  async function copiar(que: 'comando' | 'bloque') {
    if (!estado?.token) return
    const texto = que === 'comando'
      ? comando(estado.token)
      : bloqueParaIA(estado.token, proyectoNombre)
    try {
      await navigator.clipboard.writeText(texto)
      setCopiado(que)
      setTimeout(() => setCopiado(null), 2500)
    } catch {
      setError('El navegador no dejó copiar. Mostrá la llave y copiala a mano.')
    }
  }

  if (cargando) return <p className="text-muted">Cargando…</p>

  const token = estado?.token ?? null

  return (
    <div className="card" style={{ maxWidth: 820 }}>
      <h3 style={{ marginTop: 0 }}>Acceso para agentes de IA</h3>

      <p className="text-muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
        El código de este proyecto vive en otro repositorio, con su propio chat de IA que
        no ve esta lista de tareas. Con esta llave puede <b>leer el backlog y marcar
        tareas como terminadas</b> — nada más. No ve ningún otro proyecto.
      </p>

      {estado && (
        <p className="text-muted" style={{ fontSize: 13 }}>
          Hoy hay <b>{estado.tareas_abiertas}</b> {estado.tareas_abiertas === 1 ? 'tarea abierta' : 'tareas abiertas'} en este proyecto.
        </p>
      )}

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>
          <IconAlertTriangle size={13} /> {error}
        </p>
      )}

      {!token ? (
        /* Caso raro: todo proyecto nace con llave. Si no la tiene es porque
           alguien la revocó a propósito, así que se dice eso y no "todavía no
           se generó", que mandaría a pensar en un error. */
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 14 }}>
            Este proyecto no tiene llave: fue revocada. Mientras siga así, ningún chat
            puede leer su backlog.
          </p>
          <Button onClick={generar}>Generar una nueva</Button>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 18, marginBottom: 6 }}>
            <label className="form-label">Llave</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code className="codigo-llave">
                {visible ? token : '•'.repeat(32)}
              </code>
              <button className="btn btn-secondary btn-sm" onClick={() => setVisible(v => !v)}>
                {visible ? <><IconEyeOff size={13} /> Ocultar</> : <><IconEye size={13} /> Ver</>}
              </button>
            </div>
            {estado?.generada_en && (
              <p className="text-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                Generada el {new Date(estado.generada_en).toLocaleDateString('es-PE')}.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            <Button onClick={() => copiar('bloque')}>
              {copiado === 'bloque' ? <><IconCheck size={14} /> Copiado</> : <><IconCopy size={14} /> Copiar acceso + instrucciones</>}
            </Button>
            <button className="btn btn-secondary" onClick={() => copiar('comando')}>
              {copiado === 'comando' ? <><IconCheck size={14} /> Copiado</> : <><IconCopy size={14} /> Copiar sólo el comando</>}
            </button>
          </div>

          <p className="text-muted" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.6 }}>
            El primero copia un bloque listo para pegar en el <code>CLAUDE.md</code> o
            <code> AGENTS.md</code> del otro chat: incluye cómo leer, cómo cerrar y las
            advertencias. El segundo copia sólo el comando.
          </p>

          <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--border)' }} />

          {confirmando ? (
            <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
              <p style={{ fontSize: 14, marginTop: 0 }}>
                {confirmando === 'regenerar'
                  ? 'La llave actual deja de funcionar en el acto. El chat que la tenga va a dejar de ver el backlog hasta que le pases la nueva.'
                  : 'La llave deja de funcionar y este proyecto queda sin acceso para agentes.'}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={confirmando === 'regenerar' ? generar : revocar}>
                  Sí, {confirmando === 'regenerar' ? 'regenerar' : 'revocar'}
                </Button>
                <button className="btn btn-secondary" onClick={() => setConfirmando(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmando('regenerar')}>
                <IconRefresh size={13} /> Regenerar
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmando('revocar')}>
                <IconX size={13} /> Revocar
              </button>
              <span className="text-muted" style={{ fontSize: 12.5, alignSelf: 'center' }}>
                Usalos si la llave se filtró o alguien dejó el proyecto.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}
